import _merge from 'lodash/merge';
import { Lightning } from '@lightningjs/sdk';
import { RENDERING_CONTEXT } from '@bedrock-rdk/constants';
import FocusShader from '../shaders/focus.shader';
import ImageShader from '../shaders/image.shader';
import { DETACHED_SIGNAL, INACTIVE_SIGNAL, SELECTED_SIGNAL } from '../signals';
import { areContentItemsEqual, areContextsEqual, getTemplateEntry, ICON_POSITION } from '../utils/templates';
import { getProgress } from '../progress/progress.utils';
import Incentive from '../incentive/incentive.component';
import { getLinesCount } from '../incentive/incentive.utils';
import { ALIGNMENT_VERTICAL } from '../incentive/incentive.constants';
import { BORDER_WIDTH } from '../utils/focus';
import { templates, sizes, imageOptions, ICONS_TYPES, DISPLAY_PROGRESS, DISPLAY_TEXT } from './portrait.constants';
import {
  getInitialStyle,
  defaultContext,
  getIconViewBox,
  getFocusedStyle,
  unFocusedStyle,
  getIcon,
  TEXT_MARGIN_BOTTOM,
  PROGRESS_HEIGHT,
} from './portrait.style';

/**
 * The Portrait item is used to display a program. It's a 2:3 ratio format.
 *
 * @version 2.5.1
 * @see https://zeroheight.com/726d5c282/p/38a720-portrait--v251
 */
export default class Portrait extends Lightning.Component {
  static _template() {
    return _merge(
      {
        Border: {
          rect: true,
          shader: {
            type: FocusShader,
          },
        },
        Image: {
          shader: {
            type: ImageShader,
          },
        },
        TopLeftIcon: {
          type: ICONS_TYPES[ICON_POSITION.TOP_LEFT],
        },
        TopRightIcon: {
          type: ICONS_TYPES[ICON_POSITION.TOP_RIGHT],
        },
        BottomRightIcon: {
          type: ICONS_TYPES[ICON_POSITION.BOTTOM_RIGHT],
        },
        Incentive: {
          type: Incentive,
        },
      },
      getInitialStyle(),
    );
  }

  _construct() {
    this._style = defaultContext;
  }

  _inactive() {
    this.signal(INACTIVE_SIGNAL, this);
  }

  _detach() {
    this._item = undefined;
    this.signal(DETACHED_SIGNAL, this);
  }

  get item() {
    return this._item;
  }

  set item({ content, config, template, colorScheme, renderingContext = RENDERING_CONTEXT.GENERAL }) {
    const progress = DISPLAY_PROGRESS ? getProgress(content.progress) : 0;

    if (areContentItemsEqual(this._item, content) && areContextsEqual(this._renderingContext, renderingContext)) {
      this._item = content;
      // A three level patch object to move one number: the shader is already there, so it is written directly
      this.tag('Image').shader.progress = progress;

      return;
    }

    this._renderingContext = renderingContext;
    this._item = content;

    const { screenSize } = this._style;
    this._config = config || getTemplateEntry(sizes, template, renderingContext, screenSize);

    const { h } = this._config;
    const { pictos, incentives, image } = this._item;
    const extraTitle = DISPLAY_TEXT ? this._item.extraTitle : '';
    const imageUrl = (image && image.src) || '';
    const viewBox = getIconViewBox(screenSize);
    const incentiveLines = getLinesCount(incentives);
    if (colorScheme && colorScheme !== this._style.colorMode) {
      this._style = { screenSize, colorMode: colorScheme };
    }

    const portraitImageOptions = getTemplateEntry(imageOptions, template, renderingContext, screenSize);
    const { PRIMARY: primaryImage } = portraitImageOptions;
    const offset = BORDER_WIDTH[screenSize];

    this.patch({
      w: this._config.w,
      h: this._config.h,
      Border: {
        w: primaryImage.width + offset * 2,
        h: primaryImage.height + offset * 2,
      },
      Image: {
        w: primaryImage.width,
        h: primaryImage.height,
        src: imageUrl,
        shader: {
          progress,
        },
      },
      Text: {
        y: h - (progress ? PROGRESS_HEIGHT : 0) - TEXT_MARGIN_BOTTOM,
        text: {
          text: extraTitle || '',
        },
      },
      TopLeftIcon: {
        iconData: {
          picto: getIcon(pictos, ICON_POSITION.TOP_LEFT),
          viewBox,
        },
      },
      TopRightIcon: {
        iconData: {
          picto: getIcon(pictos, ICON_POSITION.TOP_RIGHT),
          viewBox,
        },
      },
      BottomRightIcon: {
        iconData: {
          picto: getIcon(pictos, ICON_POSITION.BOTTOM_RIGHT),
          viewBox,
        },
      },
      Incentive: {
        visible: incentiveLines > 0,
        data: {
          w: this._config.w,
          linesCount: incentiveLines,
          incentives,
          colorMode: this._style.colorMode,
          alignment: ALIGNMENT_VERTICAL,
        },
      },
    });
  }

  _focus() {
    this.patch(getFocusedStyle(this._style));
  }

  _unfocus() {
    this.patch(unFocusedStyle);
  }

  _handleEnter() {
    this.signal(SELECTED_SIGNAL, this._item);

    return true;
  }
}

Portrait.Templates = templates;
Portrait.Sizes = sizes;
Portrait.ImageOptions = imageOptions;
